import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Checkbox, Flex, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  AdditionalFileModal,
  AuthorizedButton,
  Container,
  DataTable,
  PageTitle,
  PolicyModal,
  SExportButton,
  SRangePicker,
} from '../../../components';
import { INITIAL_VALUES } from './const';
import styles from './index.module.scss';
import globalStyles from '../../../assets/styles/index.module.scss';
import UseHttpService from '../../../services/hook';
import { useAppDispatch, useAppSelector } from '../../../store';
import validationSchema from './validations';
import { getColumns } from './columns';
import { tableActions } from '../../../store/reducers/tableReducer';
import { IEnum } from '../../../services/type';
import { IFile, IPolicyDataSource, IPolicyInitialValues } from '../../../types';
import { modalTypes } from '../../../assets/enums';
import { downloadFile } from '../../../helpers/File';
import { cleanupAfterSubmit, prepareModalData } from '../../../utils/functions';
import useFetchParameters from '../../../hooks';
import PolicyService from '../../../api-clients/PolicyService';
import EnumService from '../../../api-clients/EnumService';

const Policy: FC = () => {
  const dispatch = useAppDispatch();
  const executeService = UseHttpService();
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );

  // Redux State
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);

  // State
  const [selectedPolicy, setSelectedPolicy] = useState<
    IPolicyDataSource | undefined
  >(undefined);
  const [additionalFile, setAdditionalFile] = useState<IFile[] | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<IPolicyDataSource[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [isExpired, setIsExpired] = useState<boolean | undefined>(undefined);
  const [policyTypes, setPolicyTypes] = useState<IEnum[]>([]);
  const [additionalFileID, setAdditionalFileID] = useState<number | undefined>(
    undefined,
  );
  const [tempAdditionalFiles, setTempAdditionalFiles] = useState<
    FileList | undefined
  >(undefined);

  const parameters = useFetchParameters({
    search,
    sort,
    page,
    dates,
    isExpired,
  });
  const policyService = useMemo(
    () => new PolicyService(executeService, parameters),
    [executeService, parameters],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  // Handlers
  const handleFetchEnums = async () => {
    const response = await enumService.fetchPolicyTypes();
    if (response && Array.isArray(response)) {
      setPolicyTypes(response);
    }
  };

  const handleFetchData = async () => {
    const response = await policyService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      setDataSource(response.results);
      if (additionalFileID) {
        const item = response.results.find(
          (item) => item.id === additionalFileID,
        );
        if (item) {
          setAdditionalFileID(item.id);
          setAdditionalFile(item.additional_files);
        }
      }
    }
  };

  const handleChangeExpired = () => setIsExpired((prev) => !prev);

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const selected = await policyService.fetchItemById(id);
    if (selected && 'id' in selected) {
      setSelectedPolicy(selected);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedPolicy(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleUploadFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      formik.setFieldValue('upload_files', e.target.files[0]);
    }
  };

  const handleClickDelete = async (id: number) => {
    try {
      await policyService.deleteItemById(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IPolicyDataSource>(
        setIsModalOpen,
        setSelectedPolicy,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickExport = async () => {
    const response = await policyService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (values: IPolicyInitialValues) => {
    try {
      await policyService.submitForm(values, selectedPolicy);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IPolicyDataSource>(
        setIsModalOpen,
        setSelectedPolicy,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleSetAdditionalFile = (id: number, file: IFile[]) => {
    setAdditionalFileID(id);
    setAdditionalFile(file);
  };

  const handleUploadAdditionalFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAdditionalFiles(e.target.files);
    }
  };

  const handleCloseAdditionalFile = () => {
    setAdditionalFileID(undefined);
    setAdditionalFile(undefined);
    setTempAdditionalFiles(undefined);
  };

  const handleDeleteFile = async (id: number, fileId: number) => {
    await policyService.deleteFile(id, fileId);
    handleFetchData();
  };

  const handleSubmitFile = async () => {
    if (additionalFileID && tempAdditionalFiles) {
      await policyService.uploadFiles(additionalFileID, tempAdditionalFiles);
      setTempAdditionalFiles(undefined);
      handleFetchData();
    }
  };

  // Columns
  const columns = getColumns(
    handleClickDelete,
    handleOpenEditModal,
    handleSetAdditionalFile,
    organizations,
    policyTypes,
  );

  // Form Management
  const formik = useFormik<IPolicyInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (selectedPolicy) {
      const result = prepareModalData<IPolicyDataSource, IPolicyInitialValues>(
        selectedPolicy,
      );
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedPolicy]);

  useEffect(() => {
    handleFetchData();
  }, [isExpired, dates, search, sort.key, sort.order, page.number, page.size]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Nakliyat Poliçeler" />
      <Flex vertical gap="middle">
        <Flex justify="space-between">
          <Flex vertical gap="small">
            <SRangePicker
              placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
              onChange={(_, dates: [string, string]) => setDates(dates)}
            />
            <Flex gap="small">
              <Checkbox checked={isExpired} onChange={handleChangeExpired} />
              <Typography className={globalStyles.outOfDateText}>
                Sadece Aktif Poliçeleri Göster
              </Typography>
            </Flex>
          </Flex>
          <Flex gap={5}>
            <SExportButton onClick={handleClickExport} />
            <AuthorizedButton
              model="transportationpolicy"
              action="add"
              className={styles.button}
              onClick={handleOpenModal}
            >
              <PlusOutlined className={styles.buttonIcon} />
              <div className={styles.buttonText}>Yeni Poliçe Talebi</div>
            </AuthorizedButton>
          </Flex>
        </Flex>
        <DataTable<IPolicyDataSource>
          columns={columns}
          dataSource={dataSource}
        />
      </Flex>
      <PolicyModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Yeni Nakliyat Poliçesi Oluşturma"
        subTitle="Yeni Nakliyat Poliçesi Oluşturma"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
        policyTypes={policyTypes}
      />
      <PolicyModal
        type={modalTypes.EDIT}
        open={!!selectedPolicy}
        title="Nakliyat Poliçesi Güncelleme"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
        policyTypes={policyTypes}
      />
      <AdditionalFileModal
        open={!!additionalFileID && Array.isArray(additionalFile)}
        model="transportationpolicyadditionalfile"
        tempAdditionalFiles={tempAdditionalFiles}
        additionalFiles={additionalFile}
        handleUploadFile={handleUploadAdditionalFile}
        handleDeleteFile={handleDeleteFile}
        handleSubmit={handleSubmitFile}
        handleCancel={handleCloseAdditionalFile}
      />
    </Container>
  );
};

export default Policy;
